import { forwardRef, Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { UI } from '@/Components/UI';
import { useApi, useDebounce } from '@/hooks';
import { Tag } from '@/types';
import { FilterDialog } from './FilterDialog';
import { useTranslation } from 'react-i18next';

type TagFilterDialogProps = {
    value: string[] | undefined;
    onChange: (Tag: string[]) => void;
}

type TagFilterContentProps = Omit<TagFilterDialogProps, "onChange">;

type TagFilterContentRef = {
    getSelectedTags: () => string[];
}

export const TagFilter = ({ value, onChange }: TagFilterDialogProps) => {
    const { t } = useTranslation(['search']);

    const [isOpen, setIsOpen] = useState(false);
    const contentRef = useRef<TagFilterContentRef>(undefined);

    function apply() {
        const selectedTag = contentRef.current?.getSelectedTags();
        onChange(selectedTag ?? []);
        setIsOpen(false);
    }

    return (
        <>
            <UI.Button
                buttonStyle={(value?.length ?? 0) > 0 ? 'tonal' : 'outline'}
                className={'py-1'}
                onClick={() => setIsOpen(true)}>
                <UI.Icon name={'tag'}/>&nbsp;{t('filters.tags.title', {count: value?.length ?? 0})}
            </UI.Button>

            <FilterDialog
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                onApply={apply}>
                <TagFilterDialogContent
                    ref={contentRef}
                    value={value} />
            </FilterDialog>
        </>
    );
}

const TagFilterDialogContent = forwardRef(
    ({ value }: TagFilterContentProps, ref: Ref<TagFilterContentRef | undefined>) => {
        const { t } = useTranslation('misc');

        const { SearchService } = useApi();
        const [search, setSearch] = useState('');
        const throttledSearch = useDebounce(search, 500);

        const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
        const [searchResult, setSearchResult] = useState<Tag[]>([]);

        // Initial load of selected Tag
        useEffect(() => {
            // No Tag selected
            if (value === undefined || value.length === 0) {
                return;
            }

            SearchService
                .listTags(value)
                .then((response) => setSelectedTags(response.data))
                .catch(console.error);
        }, []);

        // Load new search results
        useEffect(() => {
            SearchService
                .searchTags(throttledSearch)
                .then((response) => setSearchResult(response.data))
                .catch(console.error);
        }, [throttledSearch]);

        useImperativeHandle(
            ref,
            () => ({
                getSelectedTags: () =>
                    selectedTags.map((Tag) => Tag.id).filter((id) => id !== undefined)
            }),
            [selectedTags]
        );

        const onSelected = (Tag: Tag) => {
            if (selectedTags.some((item) => item.id === Tag.id)) {
                // Remove the Tag from the selected list
                setSelectedTags(selectedTags.filter((item) => item.id !== Tag.id));
            } else {
                // Add the Tag to the selected list
                setSelectedTags([...selectedTags, Tag]);
            }
        };

        return (
            <>
                <UI.Form.SearchInput
                    value={search}
                    onChange={setSearch} />

                <div className={'flex flex-row gap-2 overflow-x-scroll'}>
                    {selectedTags.length === 0 && (
                        <UI.Button
                            buttonStyle={'text'}
                            disabled>
                            {t('noneSelected')}
                        </UI.Button>
                    )}

                    {selectedTags.map((item) => (
                        <UI.Button
                            key={item.id}
                            className={'flex flex-row items-center gap-2 whitespace-nowrap'}
                            onClick={() => onSelected(item)}>
                            <UI.Icon name={'close'} />

                            <span>
                                {item.name}
                            </span>
                        </UI.Button>
                    ))}
                </div>

                <div className={'flex flex-col gap-1 mt-2'}>
                    {searchResult.map((item) => (
                        <UI.Button
                            key={item.id}
                            buttonStyle={selectedTags.some((tag) => tag.id === item.id) ? 'filled' : 'tonal'}
                            onClick={() => onSelected(item)}
                            className={'w-full rounded-none first:rounded-t-xl last:rounded-b-xl'}>
                            {item.name}
                        </UI.Button>
                    ))}
                </div>
            </>
        );
    });
